import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import OwnerPageLayout from '../components/layout/OwnerPageLayout';
import { setSelectedMemberSchoolId } from '../components/MemberSchoolSwitcher';
import { useTranslation } from 'react-i18next';


const API = process.env.REACT_APP_API_URL || 'http://localhost:5037/api';

export default function SchoolPaymentSuccessPage() {
    const { t } = useTranslation(['balance', 'common', 'nav', 'messages', 'errors']);
    const [searchParams] = useSearchParams();
    const schoolId = searchParams.get('schoolId');
    const studentPaymentId = searchParams.get('studentPaymentId');

    const [loading, setLoading] = useState(true);
    const [payment, setPayment] = useState(null);
    const [balance, setBalance] = useState(null);
    const [error, setError] = useState(null);

    const token = localStorage.getItem('token');
    const api = axios.create({
        baseURL: API,
        headers: { Authorization: `Bearer ${token}` }
    });

    const syncDoneRef = useRef(false);

    useEffect(() => {
        if (syncDoneRef.current) return;
        syncDoneRef.current = true;
        loadResult();
    }, [schoolId, studentPaymentId]);

    async function loadResult() {
        if (!schoolId || !studentPaymentId) {
            setError(t('balance:paymentSuccess.invalidLink'));
            setLoading(false);
            return;
        }

        try {
            setSelectedMemberSchoolId(parseInt(schoolId, 10));
            const syncKey = `school-payment-sync-one-${schoolId}-${studentPaymentId}`;
            if (!sessionStorage.getItem(syncKey)) {
                try {
                    await api.post(`/schools/${schoolId}/payments/${studentPaymentId}/sync`);
                    sessionStorage.setItem(syncKey, '1');
                } catch (syncErr) {
                    console.warn('payment sync', syncErr);
                }
            }

            const payRes = await api.get(`/schools/${schoolId}/payments`);
            const list = payRes.data || [];
            const found = list.find(p => String(p.id) === String(studentPaymentId));
            setPayment(found || null);

            const profilesRes = await api.get('/student/me');
            const profile = (profilesRes.data || []).find(
                p => String(p.schoolId) === String(schoolId)
            ) || profilesRes.data?.[0];
            if (profile) {
                const balRes = await api.get(`/subscription/balance?schoolId=${schoolId}`);
                setBalance(balRes.data.balance);
            }
        } catch (err) {
            setError(err.response?.data?.message || t('balance:paymentSuccess.checkFailed'));
        }
        setLoading(false);
    }

    const succeeded = payment?.status === 'succeeded';
    const pending = payment?.status === 'pending';

    const pageTitle = loading
        ? t('balance:paymentSuccess.checking')
        : error
            ? t('common:error')
            : succeeded
                ? t('balance:paymentSuccess.successTitle')
                : pending
                    ? t('balance:paymentSuccess.pendingTitle')
                    : t('balance:paymentSuccess.statusTitle');

    return (
        <OwnerPageLayout title={pageTitle} maxWidth={560}>
            <div style={{ padding: '24px 0', textAlign: 'center' }}>
                {loading && (
                    <>
                        <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
                        <h2 style={{ margin: '0 0 12px', fontSize: '22px', fontWeight: '800' }}>
                            {t('balance:paymentSuccess.checking')}
                        </h2>
                    </>
                )}

                {!loading && error && (
                    <>
                        <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
                        <h2 style={{ margin: '0 0 12px', fontSize: '22px', fontWeight: '800' }}>{t('common:error')}</h2>
                        <p style={{ color: '#666', fontSize: '14px', marginBottom: '24px' }}>{error}</p>
                    </>
                )}

                {!loading && !error && succeeded && (
                    <>
                        <div style={{ fontSize: '48px', marginBottom: '16px' }}>✅</div>
                        <h2 style={{ margin: '0 0 12px', fontSize: '22px', fontWeight: '800' }}>
                            {t('balance:paymentSuccess.successTitle')}
                        </h2>
                        <p style={{ color: '#666', fontSize: '14px', marginBottom: '8px' }}>
                            {t('balance:paymentSuccess.credited', { amount: `${(payment.amount || 0).toLocaleString()} ₽` })}
                        </p>
                        {balance != null && (
                            <p style={{ color: '#2e7d32', fontSize: '15px', fontWeight: '700', marginBottom: '24px' }}>
                                {t('balance:paymentSuccess.balanceLabel', {
                                    balance: `${(balance >= 0 ? '+' : '')}${balance.toLocaleString()} ₽`
                                })}
                            </p>
                        )}
                    </>
                )}

                {!loading && !error && pending && (
                    <>
                        <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
                        <h2 style={{ margin: '0 0 12px', fontSize: '22px', fontWeight: '800' }}>
                            {t('balance:paymentSuccess.processingTitle')}
                        </h2>
                        <p style={{ color: '#666', fontSize: '14px', marginBottom: '24px' }}>
                            {t('balance:paymentSuccess.processingHint')}
                        </p>
                    </>
                )}

                {!loading && !error && !succeeded && !pending && (
                    <>
                        <div style={{ fontSize: '48px', marginBottom: '16px' }}>ℹ️</div>
                        <h2 style={{ margin: '0 0 12px', fontSize: '22px', fontWeight: '800' }}>
                            {t('balance:paymentSuccess.returnTitle')}
                        </h2>
                        <p style={{ color: '#666', fontSize: '14px', marginBottom: '24px' }}>
                            {payment
                                ? t('balance:paymentSuccess.paymentStatus', { status: payment.status })
                                : t('balance:paymentSuccess.paymentNotFound')}
                        </p>
                    </>
                )}

                <Link
                    to="/my-balance"
                    style={{
                        display: 'inline-block', padding: '14px 28px', background: '#EB3333',
                        color: 'white', borderRadius: '10px', textDecoration: 'none',
                        fontWeight: '700', fontSize: '15px'
                    }}
                >
                    {t('balance:paymentSuccess.toBalance')}
                </Link>

                {!loading && pending && (
                    <button
                        type="button"
                        onClick={() => { setLoading(true); loadResult(); }}
                        style={{
                            display: 'block', margin: '16px auto 0', padding: '10px 20px',
                            border: '1px solid #EB3333', background: 'white', color: '#EB3333',
                            borderRadius: '10px', cursor: 'pointer', fontWeight: '600'
                        }}
                    >
                        {t('balance:paymentSuccess.refreshStatus')}
                    </button>
                )}
            </div>
        </OwnerPageLayout>
    );
}
